/**
 * Marketing host — canonical URL target for all public pages. The same
 * page is reachable under `app.gutscheinpro.de/...` too (Shopify admin
 * requests land there first), but search engines should only index the
 * marketing-host copy. Hardcoded so `canonical` stays stable regardless
 * of the request host.
 */
const MARKETING_HOST = "https://gutscheinpro.de";
const DEFAULT_OG_IMAGE = `${MARKETING_HOST}/ig-image.png`;

export interface SeoMetaInput {
  /** Path on the marketing host, including leading slash. */
  path: string;
  /** Full <title> text. */
  title: string;
  /** Optional <meta description>. Also used for OG + Twitter description. */
  description?: string;
  /** Optional OG image override (absolute URL). Defaults to /logo.png. */
  ogImage?: string;
  /** Default "article". Set "website" for the home page. */
  ogType?: string;
  /** When true, emits `noindex, nofollow` instead of `index, follow`. */
  noindex?: boolean;
}

/**
 * Build a consistent SEO descriptor set — title, description, canonical,
 * robots, Open Graph, Twitter Card. Any marketing-surface page (home,
 * /docs, /legal/*) should use this so we don't drift.
 */
export function seoMeta({
  path,
  title,
  description,
  ogImage,
  ogType = "article",
  noindex = false,
}: SeoMetaInput) {
  const url = `${MARKETING_HOST}${path}`;
  const image = ogImage ?? DEFAULT_OG_IMAGE;

  const tags: Array<Record<string, unknown>> = [
    { title },
    {
      name: "robots",
      content: noindex ? "noindex, nofollow" : "index, follow",
    },
    { tagName: "link", rel: "canonical", href: url },
    { property: "og:type", content: ogType },
    { property: "og:site_name", content: "Gutschein Pro" },
    { property: "og:locale", content: "de_DE" },
    { property: "og:url", content: url },
    { property: "og:title", content: title },
    { property: "og:image", content: image },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:image", content: image },
  ];

  if (description) {
    tags.push({ name: "description", content: description });
    tags.push({ property: "og:description", content: description });
    tags.push({ name: "twitter:description", content: description });
  }

  return tags;
}
